import { clsx } from 'clsx';
import { Button, Flex, Text } from '@nula/core-ui';
import styles from './calendar-step.module.css';

export type CalendarProvider = 'google' | 'microsoft';

export interface CalendarStepProps {
  /** Which providers are already connected (lights the Connected badge). */
  connected: Record<CalendarProvider, boolean>;
  /** Start the OAuth flow for a provider. */
  onConnect: (provider: CalendarProvider) => void;
  onContinue: () => void;
  onSkip: () => void;
}

// SVG icons — inline SVG children are an allowed exception per CLAUDE.md.
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <polyline points="2,8 6,12 14,4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path fill="#F25022" d="M3 3h8.5v8.5H3z" />
    <path fill="#7FBA00" d="M12.5 3H21v8.5h-8.5z" />
    <path fill="#00A4EF" d="M3 12.5h8.5V21H3z" />
    <path fill="#FFB900" d="M12.5 12.5H21V21h-8.5z" />
  </svg>
);

interface ProviderRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  provider: CalendarProvider;
  connected: boolean;
  onConnect: (provider: CalendarProvider) => void;
}

function ProviderRow({ icon, title, description, provider, connected, onConnect }: ProviderRowProps) {
  return (
    <Flex align="center" gap="md" className={clsx(styles.providerRow, connected && styles.providerRowConnected)}>
      <span className={styles.providerIcon}>{icon}</span>
      <Flex vertical gap="xs" className={styles.providerInfo}>
        <Text className={styles.providerTitle}>{title}</Text>
        <Text type="secondary" size="sm" className={styles.providerDesc}>{description}</Text>
      </Flex>
      {connected ? (
        <Flex align="center" gap="xs" className={styles.connectedBadge} data-testid={`calendar-connected-${provider}`}>
          <CheckIcon />
          Connected
        </Flex>
      ) : (
        <Button
          variant="default"
          size="sm"
          onClick={() => onConnect(provider)}
          className={styles.connectBtn}
          aria-label={`Connect ${title}`}
        >
          Connect
        </Button>
      )}
    </Flex>
  );
}

export function CalendarStep({ connected, onConnect, onContinue, onSkip }: CalendarStepProps) {
  return (
    <Flex vertical gap="lg">
      <Flex vertical>
        <Text className={styles.eyebrow}>Stay in sync</Text>
        <Text className={styles.title}>
          Connect your <em className={styles.titleEm}>calendar.</em>
        </Text>
        <Text type="secondary" className={styles.subtitle}>
          Nula reads your meeting schedule so it can prep notes and start recording at the
          right moment. It only reads events and never edits them.
        </Text>
      </Flex>

      <Flex vertical gap="sm">
        <ProviderRow
          icon={<GoogleIcon />}
          title="Google Calendar"
          description="Connect a Google account to sync meetings and attendees."
          provider="google"
          connected={connected.google}
          onConnect={onConnect}
        />
        <ProviderRow
          icon={<MicrosoftIcon />}
          title="Microsoft / Outlook"
          description="Connect a Microsoft 365 or Outlook account to sync your calendar."
          provider="microsoft"
          connected={connected.microsoft}
          onConnect={onConnect}
        />
      </Flex>

      <Flex align="center" gap="md">
        <Button variant="primary" size="lg" block onClick={onContinue}>Continue</Button>
        <Button variant="text" size="sm" onClick={onSkip} className={styles.skip}>Skip</Button>
      </Flex>
    </Flex>
  );
}
